import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Retry policy for the boot-critical (and all other idempotent) RTK Query
 * fetches. Wraps `baseQuery` with `retry()` so a transient upstream blip during
 * boot — the BFF momentarily unable to reach a microservice — recovers
 * automatically instead of stranding the user on the splash error page
 * (CUSTOMER-UI-6H) or surfacing an `[Infra]` event (CUSTOMER-UI-6J). A query
 * that recovers on retry produces no Sentry event at all (capture runs only on
 * the final result in `baseQuery`); only a sustained outage reports once.
 *
 * Deliberately narrow on TWO axes so it can never mask a real bug or cause a
 * duplicate write:
 *  - method: only idempotent GET/HEAD retry. A mutation (POST/PUT/PATCH/DELETE)
 *    that 502s may have already been applied upstream — retrying could
 *    double-charge a payment.
 *  - error shape: only TRANSIENT infra failures retry (see isTransientInfraError).
 *    A 4xx won't self-heal; a plain 500 may be a deterministic app bug.
 */

/** Maximum number of automatic retries (in addition to the initial attempt). */
export const MAX_BOOT_RETRIES = 3;

// Gateway/proxy statuses that mean "upstream was unreachable", not "the request
// was bad" — always worth a retry with backoff.
// Exported so `sentryErrorCapture` decides what "the request never reached the
// BFF" means from the SAME list: if the two drifted, an error could be retried
// as transient while being reported as a permanent bug, or the reverse.
export const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);
// Transient 4xx: request timeout + rate-limit. Backoff is exactly the remedy.
const TRANSIENT_4XX: ReadonlySet<number> = new Set([408, 429]);
/**
 * Node transport-failure codes meaning the BFF got no response from an
 * upstream at all. The BFF puts the code at `error.error.code` of its 5xx
 * `ExceptionDetail` body (`ExceptionDetail.error` holds the whole detail it was
 * built from, whose own `error` is the sanitised `{ code, status }` summary).
 */
export const TRANSPORT_FAILURE_CODES = [
  "ECONNREFUSED",
  "ECONNRESET",
  "ETIMEDOUT",
  "ENOTFOUND",
] as const;

export type TransportFailureCode = (typeof TRANSPORT_FAILURE_CODES)[number];

/**
 * A transport failure found in an error body, and where it was read from:
 * `"code"` for the structured code, `"message"` for the text fallback.
 */
export interface TransportFailure {
  code: TransportFailureCode;
  source: "code" | "message";
}

function isTransportFailureCode(value: unknown): value is TransportFailureCode {
  return TRANSPORT_FAILURE_CODES.some((code) => code === value);
}

/** `value[key]` when `value` is an object, otherwise `undefined`. */
function field(value: unknown, key: string): unknown {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

/**
 * The single rule for "did this error body report a transport failure".
 * `isTransientInfraError` (retry) and `isInfraFailure` in sentryErrorCapture.ts
 * (Sentry grouping) both call it, so the two read the code and the text the
 * same way. They do not see the same bodies: the retry side passes any 5xx
 * body, while the Sentry side passes only a body shaped like `ExceptionDetail`
 * (numeric `status`, string `origin` and `type`) and gets `null` otherwise. The
 * BFF's own no-response body always has that shape; a 5xx body missing one of
 * those fields is retried but captured as a plain 5xx.
 *
 * Reads the transport code at `body.error.error.code` first. The generated
 * type says `ExceptionDetail.error` is a string, but the BFF sends an object
 * there, so every level is checked at runtime. Falls back to finding a code in
 * the top-level `message` text, which is the only signal in bodies that carry
 * no code (Node's own wording, e.g. "connect ECONNREFUSED <address>").
 */
export function readTransportFailure(body: unknown): TransportFailure | null {
  const code = field(field(field(body, "error"), "error"), "code");
  if (isTransportFailureCode(code)) return { code, source: "code" };

  const message = field(body, "message");
  if (typeof message !== "string") return null;
  const marker = TRANSPORT_FAILURE_CODES.find((c) => message.includes(c));
  return marker ? { code: marker, source: "message" } : null;
}

function isIdempotentMethod(method: string): boolean {
  const upper = method.toUpperCase();
  return upper === "GET" || upper === "HEAD";
}

/**
 * Whether an RTK Query error is a TRANSIENT infrastructure failure that a retry
 * could plausibly clear — as opposed to a deterministic client/server error
 * that will fail identically every time.
 */
export function isTransientInfraError(error: FetchBaseQueryError): boolean {
  // Network/transport-level failures (connection refused at the edge, DNS, a
  // dropped socket) and client-side timeouts.
  if (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR") {
    return true;
  }

  // A PARSING_ERROR is only transient when it sits behind a gateway 5xx — that
  // is a proxy/WAF HTML error page (the BFF was unreachable). A parse failure
  // on a 2xx is a BFF serialization bug that will not self-heal.
  if (error.status === "PARSING_ERROR") {
    return GATEWAY_STATUSES.has(error.originalStatus);
  }

  if (typeof error.status === "number") {
    if (GATEWAY_STATUSES.has(error.status) || TRANSIENT_4XX.has(error.status)) {
      return true;
    }
    // An infra-shaped 5xx: the BFF wrapped a transport failure (ECONNREFUSED…)
    // as a 5xx with a structured body — same incident class as a raw 502/504.
    if (error.status >= 500 && error.status < 600) {
      return readTransportFailure(error.data) !== null;
    }
  }

  return false;
}

/**
 * The `retryCondition` predicate for `retry()` in baseQuery.
 *
 * @param error   the RTK Query error from the failed attempt
 * @param method  the request HTTP method (case-insensitive)
 * @param attempt RTK's 1-based retry counter (1 on the first retry decision)
 */
export function shouldRetryRequest(
  error: FetchBaseQueryError,
  method: string,
  attempt: number
): boolean {
  if (attempt > MAX_BOOT_RETRIES) return false;
  if (!isIdempotentMethod(method)) return false;
  return isTransientInfraError(error);
}
